import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { photoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Crown, Eye, Lock, Calendar } from "lucide-react";

export default function Viewers() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/me/viewers")
      .then(({ data }) => setData(data))
      .catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="container-px py-8">
        <div className="text-muted-foreground text-center py-12">Loading...</div>
      </div>
    );
  }

  const isPremium = data.is_premium;
  const count = data.count ?? 0;
  const viewers = data.viewers ?? [];

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    
    if (date.toDateString() === now.toDateString()) {
      return `Today at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday at ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }
    
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const groupViewers = (list) => {
    const today = [];
    const thisWeek = [];
    const older = [];
    
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(todayStart);
    weekStart.setDate(todayStart.getDate() - 7);
    
    list.forEach(v => {
      const date = new Date(v.last_viewed_at);
      if (date >= todayStart) {
        today.push(v);
      } else if (date >= weekStart) {
        thisWeek.push(v);
      } else {
        older.push(v);
      }
    });
    
    return { today, thisWeek, older };
  };

  const { today, thisWeek, older } = groupViewers(viewers);

  const renderGrid = (list) => (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
      {list.map((v) => (
        <Link
          to={`/app/profile/${v.user.user_id}`}
          key={v.user.user_id}
          className="card-soft p-4 flex gap-4 hover:border-primary/50 transition-all duration-300 hover:shadow-md"
          data-testid={`viewer-${v.user.user_id}`}
        >
          <img
            src={photoUrl(v.user.photos?.[0]) || "https://images.unsplash.com/photo-1535930891776-0c2dfb7fda1a?w=200"}
            alt={v.user.name}
            className="w-20 h-24 rounded-xl object-cover shrink-0 border border-primary/10"
          />
          <div className="min-w-0 flex-1 flex flex-col justify-between">
            <div>
              <div className="font-heading text-lg text-primary truncate font-bold">
                {v.user.name}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {v.user.age ? `${v.user.age} yrs · ` : ""}{v.user.district}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">
                {v.user.occupation || "Member"}
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-wider text-secondary mt-2 inline-flex items-center gap-1 font-semibold">
              <Eye className="w-3 h-3" />
              {v.view_count} view{v.view_count > 1 ? "s" : ""} · {formatTime(v.last_viewed_at)}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );

  return (
    <div className="container-px py-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-primary/10 pb-6">
        <div>
          <span className="label-cap">Profile views</span>
          <h1 className="font-heading text-4xl text-primary mt-1">Who viewed me</h1>
          <p className="text-muted-foreground mt-2">
            See members who've checked out your profile recently.
          </p>
        </div>
        {!isPremium && (
          <div className="bg-secondary/10 border border-secondary/30 text-secondary px-4 py-2 rounded-full flex items-center gap-2 text-xs font-semibold w-fit">
            <Crown className="w-4 h-4 text-secondary fill-secondary/15" />
            <span>Free account (Last 5 views only)</span>
          </div>
        )}
      </div>

      {viewers.length === 0 ? (
        <div className="mt-10 text-center py-16 border border-dashed rounded-2xl text-muted-foreground bg-muted/5">
          No profile views yet. Complete your profile to attract attention!
        </div>
      ) : (
        <div className="space-y-10 mt-8">
          {today.length > 0 && (
            <div>
              <h2 className="font-heading text-xl text-primary font-bold flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                Today
              </h2>
              {renderGrid(today)}
            </div>
          )}

          {thisWeek.length > 0 && (
            <div>
              <h2 className="font-heading text-xl text-primary font-bold flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                This Week
              </h2>
              {renderGrid(thisWeek)}
            </div>
          )}

          {older.length > 0 && (
            <div>
              <h2 className="font-heading text-xl text-primary font-bold flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-gray-400" />
                Earlier
              </h2>
              {renderGrid(older)}
            </div>
          )}

          {!isPremium && (
            <div className="mt-12 card-soft p-8 text-center relative overflow-hidden bg-gradient-to-r from-secondary/5 via-transparent to-secondary/5 border-2 border-dashed border-secondary/20">
              <div className="absolute inset-0 mandala-bg pointer-events-none opacity-5" />
              <div className="relative max-w-md mx-auto">
                <Crown className="w-10 h-10 text-secondary mx-auto mb-4" strokeWidth={1.5} />
                <h2 className="font-heading text-2xl text-primary font-bold">
                  Unlock {count > 5 ? count - 5 : ""} More Profile Views
                </h2>
                <p className="text-muted-foreground text-sm mt-2">
                  You have received {count} views total. Upgrade to Premium to see the full list of who visited your profile and get direct contact access.
                </p>
                <Button asChild className="mt-6 rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold shadow-lg shadow-secondary/20" data-testid="viewers-upgrade">
                  <Link to="/app/premium">
                    <Crown className="w-4 h-4 mr-1.5" />
                    Unlock all {count} views
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
